<script setup>
import { computed, ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import {
  useNotificationsHubStore,
  NOTIFICATION_CATEGORIES,
  EXECUTOR_TYPES,
} from 'dashboard/stores/notificationsHub';
import {
  browserNotificationPermission,
  requestBrowserNotificationPermission,
  isBrowserNotificationSupported,
} from 'dashboard/helper/browserNotification';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NotificationItem from 'dashboard/components-next/NotificationsHub/NotificationItem.vue';

const store = useNotificationsHubStore();
const currentUser = useMapGetter('getCurrentUser');

// Filters (this view doubles as an audit log).
const categoryFilter = ref('all');
const executorFilter = ref('all');
const readFilter = ref('all'); // all | unread | read
const fromDate = ref(''); // datetime-local string
const toDate = ref('');
const search = ref('');

const permission = ref(browserNotificationPermission());

const filtered = computed(() => {
  const fromTs = fromDate.value ? new Date(fromDate.value).getTime() : null;
  const toTs = toDate.value ? new Date(toDate.value).getTime() : null;
  return store.list.filter(n => {
    if (categoryFilter.value !== 'all' && n.category !== categoryFilter.value)
      return false;
    if (
      executorFilter.value !== 'all' &&
      n.executor?.type !== executorFilter.value
    )
      return false;
    if (readFilter.value === 'unread' && n.read) return false;
    if (readFilter.value === 'read' && !n.read) return false;
    if (fromTs !== null && n.timestamp < fromTs) return false;
    if (toTs !== null && n.timestamp > toTs) return false;
    if (search.value.trim()) {
      const q = search.value.trim().toLowerCase();
      const haystack =
        `${n.title} ${n.body} ${n.executor?.name || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
});

const clearDates = () => {
  fromDate.value = '';
  toDate.value = '';
};

const unreadCount = computed(() => store.unreadCount);

const enableBrowserNotifications = async () => {
  if (!isBrowserNotificationSupported()) {
    useAlert('Tu navegador no soporta notificaciones.');
    return;
  }
  const result = await requestBrowserNotificationPermission();
  permission.value = result;
  if (result === 'granted') {
    store.setBrowserEnabled(true);
    useAlert('Notificaciones del navegador activadas.');
  } else {
    store.setBrowserEnabled(false);
    useAlert('Permiso de notificaciones denegado.');
  }
};

const toggleBrowserNotifications = () => {
  if (store.browserEnabled) {
    store.setBrowserEnabled(false);
    useAlert('Notificaciones del navegador desactivadas.');
  } else {
    enableBrowserNotifications();
  }
};

const onRead = id => store.markRead(id);
const onRemove = id => store.remove(id);
const markAllRead = () => store.markAllRead();
const clearAll = () => {
  // eslint-disable-next-line no-alert
  if (window.confirm('¿Vaciar todo el historial de notificaciones?')) {
    store.clear();
  }
};

const selectClass =
  'h-8 px-2 rounded-md text-xs bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand';

const browserBtnLabel = computed(() => {
  if (store.browserEnabled) return 'Navegador: activado';
  if (permission.value === 'denied') return 'Navegador: bloqueado';
  return 'Activar navegador';
});

onMounted(() => {
  permission.value = browserNotificationPermission();
  store.setCurrentUser(currentUser.value?.id);
  store.fetch();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header -->
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0">
          Centro de notificaciones
        </h1>
        <span class="text-sm text-n-slate-11">
          Actividad del sistema · {{ unreadCount }} sin leer
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="browserBtnLabel"
          :variant="store.browserEnabled ? 'solid' : 'outline'"
          :color="store.browserEnabled ? 'teal' : 'slate'"
          size="sm"
          icon="i-lucide-monitor"
          @click="toggleBrowserNotifications"
        />
        <Button
          label="Marcar todo leído"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-check-check"
          @click="markAllRead"
        />
        <Button
          label="Vaciar"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          @click="clearAll"
        />
      </div>
    </div>

    <!-- Filters (audit) -->
    <div
      class="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">Categoría</span>
        <select v-model="categoryFilter" :class="selectClass">
          <option value="all">Todas</option>
          <option v-for="c in NOTIFICATION_CATEGORIES" :key="c" :value="c">
            {{ c }}
          </option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">Ejecutor</span>
        <select v-model="executorFilter" :class="selectClass">
          <option value="all">Todos</option>
          <option v-for="e in EXECUTOR_TYPES" :key="e" :value="e">
            {{ e }}
          </option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">Estado</span>
        <select v-model="readFilter" :class="selectClass">
          <option value="all">Todos</option>
          <option value="unread">Sin leer</option>
          <option value="read">Leídos</option>
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">Desde</span>
        <input v-model="fromDate" type="datetime-local" :class="selectClass" />
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">Hasta</span>
        <input v-model="toDate" type="datetime-local" :class="selectClass" />
      </div>
      <Button
        v-if="fromDate || toDate"
        label="Limpiar fechas"
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-calendar-x"
        @click="clearDates"
      />
      <Input
        v-model="search"
        placeholder="Buscar…"
        class="ml-auto w-56"
      />
    </div>

    <!-- List -->
    <div class="flex-1 overflow-y-auto">
      <div
        v-if="filtered.length === 0"
        class="flex flex-col items-center justify-center gap-3 h-full"
      >
        <span class="i-lucide-bell-off size-10 text-n-slate-9" />
        <span class="text-base text-n-slate-11">
          No hay notificaciones que coincidan.
        </span>
      </div>
      <div v-else class="divide-y divide-n-weak">
        <NotificationItem
          v-for="notification in filtered"
          :key="notification.id"
          :notification="notification"
          @read="onRead"
          @remove="onRemove"
        />
      </div>
    </div>
  </div>
</template>
